/* istanbul ignore file */
import React from 'react';
import { getFeatureFlags } from '@noths/polaris-client-feature-flags';
import { CookieModal } from '@noths/polaris-client-gdpr-compliance';
import { GTMWithDataLayer } from '@noths/polaris-client-google-analytics';
import { setLocaleCookies } from '@noths/polaris-client-localisation';
import type { BaseAppProps } from '@noths/polaris-client-next-components';
import { BaseApp } from '@noths/polaris-client-next-components';
import type { AppContextPlusCookies } from '@noths/polaris-dev-ts-types';
import type { AppInitialProps, AppProps } from 'next/app';
import NextApp from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { fetchGlobalContent } from 'src/services/contentstack';
import { IN_PROD } from 'src/settings';
import type { NextConfig } from 'src/types/nextConfig';
import { getServerSidePageEventObject } from 'src/utils/gaPageEvent';

const config = getConfig() as NextConfig;
const AppComponent = BaseApp(config);
const { appVersion, version } = config.publicRuntimeConfig;

const getAppComponent = (props: AppProps & BaseAppProps) => {
  // IN_DEV from the settings file will not work, the process.env.NODE_ENV check must be here for Webpack to remove the code for production
  // - https://webpack.js.org/configuration/mode/
  // - https://webpack.js.org/guides/code-splitting/
  if (process.env.NODE_ENV === 'development') {
    const MockServiceWorkerWrapper = dynamic(
      async () =>
        (
          await import(
            /* webpackChunkName: "MockServiceWorkerWrapper" */ 'src/_devtools/MockServiceWorkerWrapper'
          )
        ).MockServiceWorkerWrapper,
      {
        ssr: false,
      },
    );
    return (
      <MockServiceWorkerWrapper>
        <AppComponent {...props} />
      </MockServiceWorkerWrapper>
    );
  }
  return <AppComponent {...props} />;
};

const App = (props: AppProps & BaseAppProps) => {
  const pageEventData = getServerSidePageEventObject(version, appVersion);
  return (
    <>
      <Head>
        <title>Apply to sell with us</title>
        <CookieModal useTestScript={!IN_PROD} />
      </Head>
      <GTMWithDataLayer pageEventData={pageEventData as Record<string, unknown>} />
      {getAppComponent(props)}
    </>
  );
};

App.getInitialProps = async (
  appContext: AppContextPlusCookies,
): Promise<AppInitialProps & BaseAppProps> => {
  const {
    ctx: { req, res },
  } = appContext;

  if (req && res) {
    setLocaleCookies({ req, res });
  }

  const featureFlags = (req && getFeatureFlags(req)) || {};
  const appProps = await NextApp.getInitialProps(appContext);
  const [siteChromeContent] = await fetchGlobalContent({ featureFlags });

  return { ...appProps, siteChromeContent, featureFlags };
};

export default App;
